import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Network,
  Settings,
  Eye,
  Pencil,
  Command,
  Zap,
} from "lucide-react";
import { useWorld } from "../../api/hooks.js";
import { useWorldStore } from "../../stores/world.js";
import { ConnectionStatus } from "../common/ConnectionStatus.js";

const NAV_ITEMS = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard", shortcut: "1" },
  { path: "/topology", icon: Network, label: "Topology", shortcut: "2" },
  { path: "/settings", icon: Settings, label: "Settings", shortcut: "3" },
] as const;

export function NavigationRail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: world } = useWorld();
  const { mode, setMode } = useWorldStore();

  const statusColor =
    world?.status === "running"
      ? "bg-brand-green"
      : world?.status === "booting"
        ? "bg-status-paused"
        : world?.status === "draining"
          ? "bg-status-paused"
          : "bg-status-error";

  return (
    <nav className="w-14 bg-surface-light/60 backdrop-blur-sm border-r border-border-subtle/20 flex flex-col items-center py-3 shrink-0">
      {/* Logo */}
      <button
        onClick={() => navigate("/")}
        className="group w-9 h-9 rounded-lg flex items-center justify-center mb-6 transition-[background-color,transform] duration-200 hover:bg-surface-lighter/50 active:scale-[0.96]"
        data-tooltip="Agent World"
      >
        <Zap className="w-5 h-5 text-brand-purple transition-[color,filter] duration-300 group-hover:text-brand-cyan group-hover:drop-shadow-[0_0_8px_rgba(0,200,255,0.5)]" />
      </button>

      {/* Navigation */}
      <div className="flex flex-col gap-1 flex-1">
        {NAV_ITEMS.map((item) => {
          const isActive =
            item.path === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.path);
          const Icon = item.icon;

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`nav-tooltip w-10 h-10 rounded-lg flex items-center justify-center transition-[color,background-color,box-shadow,transform] duration-200 active:scale-[0.96] ${
                isActive
                  ? "bg-brand-purple/15 text-brand-purple shadow-glow-purple"
                  : "text-text-muted hover:text-text-primary hover:bg-surface-lighter/30"
              }`}
              data-tooltip={item.label}
            >
              <Icon className="w-[18px] h-[18px]" />
            </button>
          );
        })}
      </div>

      {/* Mode toggle */}
      <div className="flex flex-col gap-1 mb-3">
        <button
          onClick={() => setMode("monitor")}
          className={`nav-tooltip w-10 h-10 rounded-lg flex items-center justify-center transition-[color,background-color,box-shadow,transform] duration-200 active:scale-[0.96] ${
            mode === "monitor"
              ? "bg-brand-cyan/15 text-brand-cyan"
              : "text-text-muted hover:text-text-primary hover:bg-surface-lighter/30"
          }`}
          data-tooltip="Monitor"
        >
          <Eye className="w-[18px] h-[18px]" />
        </button>
        <button
          onClick={() => setMode("design")}
          className={`nav-tooltip w-10 h-10 rounded-lg flex items-center justify-center transition-[color,background-color,box-shadow,transform] duration-200 active:scale-[0.96] ${
            mode === "design"
              ? "bg-brand-pink/15 text-brand-pink"
              : "text-text-muted hover:text-text-primary hover:bg-surface-lighter/30"
          }`}
          data-tooltip="Design"
        >
          <Pencil className="w-[18px] h-[18px]" />
        </button>
      </div>

      {/* Command palette hint */}
      <button
        className="nav-tooltip w-10 h-10 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-surface-lighter/30 transition-[color,background-color,transform] duration-200 active:scale-[0.96] mb-2"
        data-tooltip="Search (Cmd+K)"
      >
        <Command className="w-4 h-4" />
      </button>

      {/* Status indicators */}
      <div className="flex flex-col items-center gap-2">
        <ConnectionStatus />
        <div
          className={`w-2 h-2 rounded-full ${statusColor} ${world?.status === "running" ? "status-pulse" : ""}`}
          title={`World: ${world?.status ?? "unknown"}`}
        />
      </div>
    </nav>
  );
}
